import { getRequestInstance } from "api/services/utils";
import { RequestTypes } from "api/services/enums";
import {
  AdvisoryDistributionGetByIdDocument,
  AdvisoryDistributionListPaginatedDocument,
  AdvisoryDistributionCreateDocument,
  AdvisoryDistributionUpdateDocument,
  AdvisoryDistributionDistributionNumberDocument,
  AdvisoryDistributionCalculationsForManagedSellingDocument,
  AdvisoryDistributionCalculationsForTokenDisbursementDocument,
} from "api/generated";

import { Optional } from "config/types";
import { executeEviction } from "api/utils/cache-eviction";
import { client } from "config/apollo";

import {
  IAdvisoryDistributionCreateVars,
  IAdvisoryDistributionPaginatedResponse,
  IAdvisoryDistributionPaginatedVars,
  IAdvisoryDistributionUpdateVars,
  IAdvisoryDistributionDetailedModel,
  IAdvisoryDistributionCalculationsForManagedSelling,
  IAdvisoryDistributionCalculationsForTokenDisbursement,
} from "./types";

export async function getAdvisoryDistribution(
  advisoryDistributionId: number
): Promise<Optional<IAdvisoryDistributionDetailedModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AdvisoryDistributionGetByIdDocument,
      variables: {
        id: advisoryDistributionId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.advisoryDistributionGetById;
  } catch (error) {
    throw error;
  }
}

export async function getAdvisoryDistributionNumber(
  advisoryProjectId: number,
  type: string,
  advisoryTrancheId?: number
): Promise<number> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AdvisoryDistributionDistributionNumberDocument,
      variables: {
        advisoryProjectId,
        type,
        advisoryTrancheId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.advisoryDistributionDistributionNumber ?? 1;
  } catch (error) {
    throw error;
  }
}

export async function getAdvisoryDistributionCalculationsForManagedSelling(
  advisoryTrancheId: number,
  amount: number,
  price?: number,
  advisoryDistributionId?: number
): Promise<IAdvisoryDistributionCalculationsForManagedSelling> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AdvisoryDistributionCalculationsForManagedSellingDocument,
      variables: {
        amount,
        price,
        advisoryDistributionId,
        advisoryTrancheId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.advisoryDistributionCalculationsForManagedSelling ?? {};
  } catch (error) {
    throw error;
  }
}

export async function getAdvisoryDistributionCalculationsForTokenDisbursement(
  advisoryProjectId: number,
  amount: number,
  price?: number,
  advisoryDistributionId?: number
): Promise<IAdvisoryDistributionCalculationsForTokenDisbursement> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AdvisoryDistributionCalculationsForTokenDisbursementDocument,
      variables: {
        amount,
        price,
        advisoryDistributionId,
        advisoryProjectId,
      },
      fetchPolicy: "network-only",
    });

    return (
      res?.data?.advisoryDistributionCalculationsForTokenDisbursement ?? {}
    );
  } catch (error) {
    throw error;
  }
}

export async function getAdvisoryDistributionPaginated(
  variables: IAdvisoryDistributionPaginatedVars
): Promise<Optional<IAdvisoryDistributionPaginatedResponse>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: AdvisoryDistributionListPaginatedDocument,
      variables,
      fetchPolicy: "network-only",
    });

    return res?.data?.advisoryDistributionListPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createAdvisoryDistribution(
  variables: IAdvisoryDistributionCreateVars
): Promise<Optional<{ advisoryDistributionId: number }>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AdvisoryDistributionCreateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    // Evict advisory distribution lists + related tranche data
    executeEviction(client, {
      typename: "AdvisoryDistribution",
      fieldNames: ["advisoryDistributionListPaginated"],
      relatedTypenames: ["AdvisoryTranche"],
    });

    return res?.data?.advisoryDistributionCreate;
  } catch (error) {
    throw error;
  }
}

export async function updateAdvisoryDistribution(
  variables: IAdvisoryDistributionUpdateVars
): Promise<Optional<{ advisoryDistributionId: number }>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: AdvisoryDistributionUpdateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    // Evict updated entity + all lists + getById queries + related tranche data
    executeEviction(client, {
      typename: "AdvisoryDistribution",
      id: variables.advisoryDistributionId.toString(),
      fieldNames: [
        "advisoryDistributionListPaginated",
        "advisoryDistributionGetById",
      ],
      relatedTypenames: ["AdvisoryTranche"],
    });

    return res?.data?.advisoryDistributionUpdate;
  } catch (error) {
    throw error;
  }
}
